import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef, MatSnackBar } from "@angular/material";
import { Observable } from "rxjs/Observable";
import { Subject } from "rxjs/Subject";
import { Subscription } from "rxjs/Subscription";
import "rxjs/add/observable/fromEvent";
import "rxjs/add/observable/merge";
import "rxjs/add/observable/zip";
import "rxjs/add/operator/debounceTime";
import "rxjs/add/operator/distinctUntilChanged";
import "rxjs/add/operator/map";
import "rxjs/add/operator/startWith";
import { fuseAnimations } from "../../../../core/animations";
import { FuseConfirmDialogComponent } from "../../../../core/components/confirm-dialog/confirm-dialog.component";
import { CategoryResult } from "../../../../core/services/categories/category.model";
import { toUTCSmallDate } from "../../../../core/services/dates.service";
import { CategoriesService } from "../categories/categories.service";
import { ClosedDatesFormDialogComponent } from "./closed-dates/closed-dates-form.component";
import { EMBARCADERE, Product, Schedule, Ticket } from "./product.model";
import { ProductService } from "./product.service";
import { TicketFormDialogComponent } from "./ticket-form/ticket-form.component";

@Component({
    selector: "fuse-product",
    templateUrl: "./product.component.html",
    styleUrls: ["./product.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class ProductComponent implements OnInit, OnDestroy {
    product = new Product();
    categories: any;
    onProductChanged: Subscription;
    refresh: Subject<any> = new Subject();
    dialogRef: any;
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    pageType: string;
    productForm: FormGroup;
    scheduleForm: FormGroup;
    ticketForm: FormGroup;
    wdForm: FormGroup;
    nwdForm: FormGroup;
    productTypes = [
        {
            title: "Croisière",
            value: "CRUISE",
        },
        {
            title: "Navette",
            value: "SHUTTLE",
        },
    ];
    fileToUpload: File = null;
    isClosingSchedules = false;

    embarcaderes = Object.values(EMBARCADERE);

    constructor(
        private productService: ProductService,
        private categoriesService: CategoriesService,
        private formBuilder: FormBuilder,
        public snackBar: MatSnackBar,
        public dialog: MatDialog,
        private location: Location
    ) {}

    ngOnInit() {
        let that = this;
        const categories$ = this.categoriesService.getCategories();
        Observable.zip(
            categories$ /* , companies$ */,
            (
                categories: CategoryResult[] /* , companies: CompanyResult[] */
            ) => ({ categories /* , companies */ })
        ).subscribe((pair) => {
            this.categories = pair.categories;
            this.onProductChanged =
                this.productService.onProductChanged.subscribe((product) => {
                    if (product) {
                        that.product = new Product(product);
                        that.pageType = "edit";
                    } else {
                        that.pageType = "new";
                        that.product = new Product();
                    }

                    that.productForm = that.createProductForm();
                });
        });
    }

    createProductForm() {
        this.scheduleForm = this.formBuilder.group({
            time: ["09:00", Validators.required],
            duration: ["01:30", Validators.required],
            embarcadere: [null, Validators.required],
        });

        this.wdForm = this.formBuilder.group({
            day: ["", Validators.required],
        });

        this.nwdForm = this.formBuilder.group({
            day: ["", Validators.required],
        });

        return this.formBuilder.group({
            _id: [this.product._id],
            type: [this.product.type, Validators.required],
            title: [this.product.title, Validators.required],
            description: [this.product.description],
            title_en: [this.product.title_en, Validators.required],
            description_en: [this.product.description_en],
            category: [
                this.product.category._id || this.product.category,
                Validators.required,
            ],
            annualRecurrence: [this.product.annualRecurrence],
            recurrenceStart: [
                this.product.recurrenceStart,
                Validators.required,
            ],
            recurrenceEnd: [this.product.recurrenceEnd, Validators.required],
            quantity: [this.product.quantity],
            tresholdAlert: [this.product.tresholdAlert],
            tresholdDiscount: [this.product.tresholdDiscount],
            tresholdFreeSeat: [
                this.product.tresholdFreeSeat,
                this.product.hidden
                    ? [Validators.required, Validators.min(0)]
                    : null,
            ],
            schedules: [this.product.schedules],
            tickets: [this.product.tickets],
            disabled: [this.product.disabled],
            hidden: [this.product.hidden],
            package: [this.product.package],
            giftcard: [this.product.giftcard],
            comment: [this.product.comment],
            workingDays: [this.product.workingDays.sort()],
            nonworkingDays: [this.product.nonworkingDays.sort()],
        });
    }

    addSchedule() {
        const newTime = this.scheduleForm.get("time").value;
        if (!newTime) {
            return;
        }
        const foundTime = this.product.schedules.find((schedule) => {
            return schedule.time === newTime;
        });
        if (foundTime) {
            this.snackBar.open("Cet horaire a déjà été enregistré", "OK", {
                verticalPosition: "top",
                duration: 2000,
            });
            return;
        }
        // console.log(this.scheduleForm.value);
        this.product.addSchedule(this.scheduleForm.value);
        this.productForm.controls["schedules"].markAsTouched();
    }

    updateSchedule() {}

    removeSchedule(schedule) {
        this.product.removeSchedule(schedule);
        this.productForm.controls["schedules"].markAsTouched();
    }

    addWorkingDay() {
        const newTime = toUTCSmallDate(this.wdForm.get("day").value);
        if (!newTime) {
            return;
        }
        // if (!this.productService.isDateValid(newTime)) {
        //     this.snackBar.open('Ce jour est hors des dates de disponibilités', 'OK', {
        //         verticalPosition: 'top',
        //         duration        : 2000
        //     });
        //     return;
        // }
        const foundTime = this.product.workingDays.find(
            (day) => day === newTime
        );
        if (foundTime) {
            this.snackBar.open("Ce jour a déjà été enregistré", "OK", {
                verticalPosition: "top",
                duration: 2000,
            });
            return;
        }
        this.product.addWorkingDay(newTime);
        this.productForm.controls["workingDays"].markAsTouched();
    }

    removeWorkingDay(day) {
        this.product.removeWorkingDay(day);
        this.productForm.controls["workingDays"].markAsTouched();
    }

    addNonWorkingDay() {
        const newTime = toUTCSmallDate(this.nwdForm.get("day").value);
        if (!newTime) {
            return;
        }
        if (!this.productService.isDateValid(newTime)) {
            this.snackBar.open(
                "Ce jour est hors des dates de disponibilités",
                "OK",
                {
                    verticalPosition: "top",
                    duration: 2000,
                }
            );
            return;
        }
        const foundTime = this.product.nonworkingDays.find((day) => {
            return day === newTime;
        });
        if (foundTime) {
            this.snackBar.open("Ce jour a déjà été enregistré", "OK", {
                verticalPosition: "top",
                duration: 2000,
            });
            return;
        }
        this.product.addNonWorkingDay(newTime);
        this.productForm.controls["nonworkingDays"].markAsTouched();
    }

    removeNonWorkingDay(day) {
        this.product.removeNonWorkingDay(day);
        this.productForm.controls["nonworkingDays"].markAsTouched();
    }

    saveProduct() {
        const data = this.productForm.getRawValue();
        this.productService
            .saveProduct(data)
            .then((response) => {
                // Trigger the subscription with new data
                this.productService.onProductChanged.next(response);

                // Show the success message
                this.snackBar.open("Produit sauvergardé", "OK", {
                    verticalPosition: "top",
                    duration: 2000,
                });
            })
            .catch((e) => {
                this.snackBar.open(
                    "Une erreur est survenue lors de la sauvegarde",
                    "OK",
                    {
                        verticalPosition: "bottom",
                        duration: 2000,
                    }
                );
            });
    }

    addProduct() {
        const data = this.productForm.getRawValue();
        this.productService
            .addProduct(data)
            .then((response) => {
                // Trigger the subscription with new data
                this.productService.onProductChanged.next(response);

                // Show the success message
                this.snackBar.open("Product ajouté", "OK", {
                    verticalPosition: "top",
                    duration: 2000,
                });

                // Change the location with new one
                this.location.go("products/" + this.product._id);
            })
            .catch((e) => {
                this.snackBar.open("Une erreur est survenue !", "OK", {
                    verticalPosition: "bottom",
                    duration: 2000,
                });
            });
    }

    removeTicket(ticket) {
        this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmMessage = `Veuillez confirmer la suppression du tarif : ${ticket.title}`;

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // const eventIndex = this.events.indexOf(event);
                // this.events.splice(eventIndex, 1);
                // this.refresh.next(true);
                this.product.removeTicket(ticket);
                this.productForm.controls["tickets"].markAsTouched();
            }
            this.confirmDialogRef = null;
        });
    }

    /**
     * Add Event
     */
    addTicket(): void {
        this.dialogRef = this.dialog.open(TicketFormDialogComponent, {
            panelClass: "ticket-form-dialog",
            data: {
                action: "new",
            },
        });
        this.dialogRef.afterClosed().subscribe((response: FormGroup) => {
            if (!response) {
                return;
            }
            const newTicket = response.getRawValue();
            delete newTicket._id;
            this.product.addTicket(newTicket);
            // this.refresh.next(true);
            this.productForm.controls["tickets"].markAsTouched();
        });
    }

    editTicket(action: string, ticket: Ticket) {
        const ticketIndex = this.product.tickets.indexOf(ticket);

        this.dialogRef = this.dialog.open(TicketFormDialogComponent, {
            panelClass: "ticket-form-dialog",
            data: {
                ticket: ticket,
                action: action,
            },
        });

        this.dialogRef.afterClosed().subscribe((response) => {
            if (!response) {
                return;
            }
            const actionType: string = response[0];
            const formData: FormGroup = response[1];
            switch (actionType) {
                case "save":
                    const ticket = formData.getRawValue();
                    if (ticket && !ticket._id) {
                        delete ticket._id;
                    }
                    this.product.tickets[ticketIndex] = Object.assign(
                        this.product.tickets[ticketIndex],
                        {
                            ...ticket,
                            amount: ticket.amountVat10 + ticket.amountVat20,
                            vatRate: 10,
                            vat10: ticket.amountVat10,
                            vat20: ticket.amountVat20,
                        }
                    );
                    // this.refresh.next(true);
                    this.productForm.controls["tickets"].markAsTouched();
                    break;
                case "delete":
                    this.removeTicket(ticket);
                    break;
            }
        });
    }

    editSchedule(schedule: Schedule) {
        const scheduleIndex = this.product.schedules.indexOf(schedule);

        this.dialogRef = this.dialog.open(ClosedDatesFormDialogComponent, {
            panelClass: "closed-dates-form-dialog",
            data: {
                schedule,
                action: "edit",
            },
        });

        this.dialogRef.afterClosed().subscribe((response) => {
            if (!response) {
                return;
            }
            const actionType: string = response[0];
            if (actionType === "save") {
                const schedule: Schedule = response[1];
                this.product.schedules[scheduleIndex] = schedule;
                this.productForm.controls["schedules"].markAsTouched();
            }
        });
    }

    closeAllSchedulesInAvailability() {
        if (!this.product || !this.product._id) {
            return;
        }

        this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmMessage =
            "Confirmer la fermeture de tous les créneaux sur toutes les dates de disponibilité à la vente ?";

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                this.confirmDialogRef = null;
                return;
            }

            this.isClosingSchedules = true;
            this.productService
                .closeAllSchedulesInAvailability(this.product._id)
                .then(() => {
                    this.snackBar.open(
                        "Tous les créneaux ont été fermés sur la période de vente.",
                        "OK",
                        {
                            verticalPosition: "top",
                            duration: 2500,
                        }
                    );
                })
                .catch(() => {
                    this.snackBar.open(
                        "Une erreur est survenue lors de la fermeture des créneaux.",
                        "OK",
                        {
                            verticalPosition: "bottom",
                            duration: 2500,
                        }
                    );
                })
                .then(() => {
                    this.isClosingSchedules = false;
                });

            this.confirmDialogRef = null;
        });
    }

    ngOnDestroy() {
        this.onProductChanged.unsubscribe();
    }

    private uploadFileSuccess(data) {
        this.snackBar.open("Image sauvegardée", "OK", {
            verticalPosition: "top",
            duration: 2000,
        });
        this.productService.getProduct();
    }

    handleFileCarteInput(files: FileList) {
        this.fileToUpload = files.item(0);
        this.uploadFileCarteToActivity();
    }

    private uploadFileCarteToActivity() {
        this.productService
            .postImageCarte(this.product._id, this.fileToUpload)
            .subscribe(
                (data) => {
                    this.uploadFileSuccess(data);
                },
                (error) => {
                    console.log(error);
                }
            );
    }

    deleteCarte() {
        this.productService.deleteCarte(this.product._id).subscribe(
            () => {
                this.deleteFileSuccess();
            },
            (error) => {
                console.log(error);
            }
        );
    }
    private deleteFileSuccess() {
        // console.log(data);
        this.snackBar.open("Image supprimée", "OK", {
            verticalPosition: "top",
            duration: 2000,
        });
        this.productService.getProduct();
    }

    getVAT(rate: number, price: number) {
        return (price / (100 + rate)) * rate;
    }
}



// WEBPACK FOOTER //
// /usr/app/src/app/main/content/app/product/product.component.ts